import type { JSX } from "react";
import React, { useEffect, useMemo, useState } from "react";

import { isEmpty } from "lodash";

import {
    Dropdown,
    DropdownButton,
    EllipsisDropdownItem,
    CircleSwitcherItem,
    Switcher,
    PixelPlaceholderLoader,
    TabPanel,
    Tabs,
    EmptyState,
} from "@similarweb/ui-components";

import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import type { ChannelKeyName, MetricKeyNames, SegmentId } from "app/constants/MmxChannels";
import { i18nFilter } from "app/filters/i18nFilter";
import { EDisplayType, displayTypeItems } from "app/pages/segments/mmx/displayTypeItems";
import type { ISegmentsMMXDataPoint, ISegmentsMMXGraphData } from "app/pages/segments/mmx/mmxGraphData";
import { SegmentDomainLegends } from "app/pages/segments/mmx/SegmentDomainLegends";
import { SwitcherGranularityContainer } from "app/pages/website-analysis/components/SwitcherGranularityContainer";
import {
    ButtonsContainer,
    LegendsTitle,
    StyledTabIcon,
} from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelAnalysisChart/StyledComponents";
import CountryService from "app/services/CountryService";
import DurationService from "app/services/DurationService";
import { PdfExportService } from "app/services/PdfExportService";
import type {
    IBaseSingleRequestParams,
    ICustomSegment,
    ICustomSegmentGroupWebsite,
} from "app/services/segments/segmentsApiService";
import SegmentsApiService, { SEGMENT_TYPES } from "app/services/segments/segmentsApiService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { allTrackers } from "app/services/track/track";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import Chart from "pro-features/components/Chart/src/Chart";
import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import markerWithDashedConfig from "pro-features/components/Chart/src/configs/series/markerWithDashedLinePerPointChartConfig";
import {
    CompareContentContainer,
    TabListStyled,
    TabsContainer,
} from "pro-features/components/Rule/src/RuleModes/EditRule/EditRuleModeStyles";
import {
    dateToUTC,
    getChartConfig,
    SegmentSubtitle,
} from "pro-features/pages/segments/components/benchmarkOvertime/SegmentsVsGroupLineChart";
import { SegmentTypeBadge } from "pro-features/pages/segments/StyledComponents";
import StyledBoxSubtitle from "pro-features/styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";
import { FlexColumn, FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { ChannelsObj } from "./ChannelsObj";
import { EngagementVerticals } from "./EngagementVerticals";
import { getAvailableGranularities } from "./MMXCommon";
import {
    MMXCompareBoxContainer,
    MMXGroupChartContainer,
    SectionContainer,
    GroupGraphContainer,
    CompareGraphLegendContainer,
    DropdownFilterContainer,
    HeaderControls,
    HeaderSwitchersGroup,
    StyledHeaderTitle,
    StyledTab,
    StyledTitleContainer,
    SwitcherContainer,
    TotalChartContainer,
} from "./styledComponents";

const SEGMENT_MMX_GROUP_DEFAULT_CHANNEL = "Direct" satisfies ChannelKeyName;

interface IDataItem {
    color: string;
    rowIndex: number;
    name: string;
    domain: string;
    Value: ISegmentsMMXDataPoint;
    seriesSubtitle: JSX.Element;
}

interface ISegmentsGroupMMXGraph {
    groups: {
        id: string;
        websites: ICustomSegmentGroupWebsite[];
    }[];
    params: any;
    data?: ISegmentsMMXGraphData;
    isLoading: any;
    selectedRows: {
        id: string;
        index: number;
        selectionColor: string;
    }[];
    selectedDisplayTypeIndex: any;
    setSelectedDisplayTypeIndex: React.Dispatch<React.SetStateAction<number>>;
    allSegments: any;
    selectedMetric: MetricKeyNames;
    setSelectedTab: any;
    selectedTab: any;
    groupData: any;
}

export const SegmentsGroupMMXGraph = (props: ISegmentsGroupMMXGraph) => {
    const {
        groups,
        params,
        data = {},
        isLoading,
        selectedRows,
        selectedDisplayTypeIndex,
        setSelectedDisplayTypeIndex,
        allSegments,
        selectedMetric,
        setSelectedTab,
        selectedTab,
        groupData,
    } = props;
    const { duration, comparedDuration, country, webSource } = params;

    const durationObject = useMemo(
        () => DurationService.getDurationData(duration, comparedDuration),
        [duration, comparedDuration],
    );

    const availableGranularities = useMemo(
        () => getAvailableGranularities(durationObject, webSource),
        [duration, webSource],
    );
    useEffect(() => {
        const durationRaw = durationObject?.raw;
        const monthDiff = durationRaw.to.diff(durationRaw.from, "month");
        if (webSource !== devicesTypes.TOTAL && monthDiff < 1 && selectedGranularity.value === "Monthly") {
            setSelectedGranularity(availableGranularities[1]);
        }
    }, [duration, webSource]);

    const [selectedGranularity, setSelectedGranularity] = useState(availableGranularities[2]);
    const apiParams = proNavigator.getApiParams(params);
    const { segmentsApiService } = useMemo(
        () => ({
            segmentsApiService: new SegmentsApiService(),
        }),
        [],
    );
    const excelUrl = useMemo(() => {
        const excelFileName = `SegmentMarketingChannels - ${groupData.name} - (${params.country}) - (${durationObject.forAPI.from}) - (${durationObject.forAPI.to})`;
        return segmentsApiService.getCustomSegmentMarketingMixGraphExcelUrl({
            ...apiParams,
            segmentGroupId: params.id,
            timeGranularity: selectedGranularity?.value,
            keys: "null",
            webSource: webSource,
            includeSubDomains: true,
            FileName: excelFileName,
        } as IBaseSingleRequestParams);
    }, [params, selectedGranularity]);

    const subtitleFilters = React.useMemo(
        () => [
            {
                filter: "date",
                value: {
                    from: durationObject.raw.from.valueOf(),
                    to: durationObject.raw.to.valueOf(),
                    useRangeDisplay:
                        duration !== "28d" &&
                        durationObject.raw.from.format("YYYY-MM") !== durationObject.raw.to.format("YYYY-MM"),
                },
            },
            {
                filter: "country",
                countryCode: country,
                value: CountryService.getCountryById(country)?.text,
            },
            {
                filter: "webSource",
                value: webSource,
            },
        ],
        [durationObject, country, webSource, duration],
    );

    const [currentChannel, setCurrentChannel] = React.useState<ChannelKeyName>(SEGMENT_MMX_GROUP_DEFAULT_CHANNEL);

    const [hiddenLegends, setHiddenLegends] = useState<string[]>([]);
    const toggleGranularity = (index: string | number) => {
        const selectedGranularity = availableGranularities[index as number];
        setSelectedGranularity(selectedGranularity);
        allTrackers.dispatchTrackEvent("Time Frame Button", "switch", "Traffic and engagement", {
            Granularity: selectedGranularity.value,
        });
    };
    const getTabProps = (index: number) => {
        return {
            onClick: () => onTabSelect(index),
            selected: selectedTab === index,
        };
    };
    const onTabSelect = (index: any) => {
        setSelectedTab(index);
        setSelectedDisplayTypeIndex(0);

        allTrackers.dispatchTrackEvent("Metric Button", "switch", "Traffic and engagement", {
            Metric: Object.keys(EngagementVerticals)[index],
        });
    };

    const chartRef = React.useRef<HTMLDivElement>(null);
    const getPNG = React.useCallback((): Promise<void> => {
        allTrackers.dispatchTrackEvent(
            "segments.mmx.group.graph.download.png",
            "submit-ok",
            "segments.mmx.group.graph.download.png",
        );
        const offSetX = 0;
        const offSetY = 50;
        const styleHTML = PdfExportService.getStylesheetsForPng();
        return PdfExportService.downloadHtmlPngFedService(
            styleHTML + chartRef.current?.outerHTML,
            "SegmentsMmxSingleGraph",
            (chartRef.current?.offsetWidth ?? 0) + offSetX,
            (chartRef.current?.offsetHeight ?? 0) + offSetY,
        ).then();
    }, []);

    const onExcelClick = () => {
        allTrackers.dispatchTrackEvent(
            "segments.mmx.compare.graph.download.excel",
            "submit-ok",
            "segments.mmx.compare.graph.download.excel",
        );
    };
    const getUtilityButtons = () => {
        return (
            <ButtonsContainer>
                {excelUrl ? (
                    <DownloadButtonMenu
                        Excel={true}
                        downloadUrl={excelUrl}
                        exportFunction={onExcelClick}
                    />
                ) : null}
                <DownloadButtonMenu
                    PNG={true}
                    handleDownloadFunction={getPNG}
                />
            </ButtonsContainer>
        );
    };
    const isPercentage =
        selectedMetric === EngagementVerticals.Visits.name && selectedDisplayTypeIndex === EDisplayType.PERCENTAGE;

    const transformedData = useMemo(() => {
        if (isLoading || selectedRows.length < 1) {
            return null;
        }
        let hasValue = false;
        const filteredChartData = Object.entries(data).reduce(
            (acc, [segId, segment]) => {
                const isWebsiteInternalReferrals =
                    SegmentsUtils.getSegmentIdTypeByKey(segId)[1] === SEGMENT_TYPES.WEBSITE &&
                    currentChannel === ChannelsObj.InternalReferrals;
                if (!isWebsiteInternalReferrals) {
                    segment[selectedGranularity.value]?.[selectedMetric]?.[currentChannel]?.Breakdown?.forEach(
                        (datePoint) => {
                            if (!hasValue && datePoint?.Value?.Value && datePoint?.Value?.Value !== 0) {
                                hasValue = true;
                            }
                            const currentRow = selectedRows?.find((row) => row.id === segId);
                            if (currentRow && !hiddenLegends.includes(segId)) {
                                const [memberObj, memberType] = SegmentsUtils.getSegmentObjectByKey(segId, {
                                    segments: allSegments,
                                    websites: groups.find((group) => group.id === params.id)?.websites,
                                });
                                switch (memberType) {
                                    case SEGMENT_TYPES.SEGMENT: {
                                        const segmentObj = memberObj as ICustomSegment;
                                        acc[segId] = {
                                            ...(acc[segId] ?? {}),
                                            [datePoint.Key]: {
                                                color: currentRow.selectionColor,
                                                rowIndex: currentRow.index,
                                                name: segmentObj.segmentName,
                                                domain: segmentObj.domain,
                                                Value: { ...datePoint.Value },
                                                seriesSubtitle: (
                                                    <SegmentSubtitle>{segmentObj.segmentName}</SegmentSubtitle>
                                                ),
                                            } as IDataItem,
                                        };
                                        break;
                                    }
                                    case SEGMENT_TYPES.WEBSITE: {
                                        const websiteObj = memberObj as ICustomSegmentGroupWebsite;
                                        acc[segId] = {
                                            ...(acc[segId] ?? {}),
                                            [datePoint.Key]: {
                                                color: currentRow.selectionColor,
                                                rowIndex: currentRow.index,
                                                name: websiteObj.domain,
                                                domain: websiteObj.domain,
                                                Value: { ...datePoint.Value },
                                                seriesSubtitle: <SegmentTypeBadge>WEBSITE</SegmentTypeBadge>,
                                            } as IDataItem,
                                        };
                                        break;
                                    }
                                }
                            }
                        },
                    );
                }
                return hasValue ? acc : {};
            },
            {} as Record<SegmentId, Record<string, IDataItem>>,
        );

        if (!hasValue) {
            return;
        }
        return transformData(filteredChartData, isPercentage).sort((a, b) => a.rowIndex - b.rowIndex);
    }, [
        selectedGranularity,
        selectedDisplayTypeIndex,
        selectedMetric,
        isLoading,
        hiddenLegends,
        currentChannel,
        selectedRows,
        data,
        allSegments,
        groups,
        params.id,
    ]);

    const getDropdown = useMemo(() => {
        const onDropdownChanged = (value: { id: ChannelKeyName }) => {
            setCurrentChannel(value.id);
            allTrackers.dispatchTrackEvent(
                "segments_analysis.marketing_channels.group.channel_dropdown",
                "click",
                "segments_analysis.marketing_channels.group.channel_dropdown",
            );
        };
        const onToggle = (isOpen: boolean) => {
            if (isOpen) {
                allTrackers.dispatchTrackEvent(
                    "segments_analysis.marketing_channels.group.channel_dropdown",
                    "open",
                    "segments_analysis.marketing_channels.group.channel_dropdown",
                );
            }
        };
        return (
            <Dropdown
                onOpen={onToggle}
                dropdownPopupPlacement={"ontop-left"}
                onToggle={onToggle}
                // @ts-expect-error Unmatching type
                selectedIds={currentChannel}
                onClick={onDropdownChanged}
                width={"245px"}
                dropdownPopupHeight={400}
            >
                {[
                    <DropdownButton
                        key={"button"}
                        width={245}
                    >
                        {currentChannel}{" "}
                    </DropdownButton>,
                    ...Object.entries(ChannelsObj).map(([channel, channelName]) => (
                        <EllipsisDropdownItem
                            // @ts-expect-error results in incorrect styles ("width: 245;" in css)
                            width={245}
                            key={channel}
                            id={channelName}
                        >
                            {channelName}
                        </EllipsisDropdownItem>
                    )),
                ]}
            </Dropdown>
        );
    }, [currentChannel]);

    const chartConfig = useMemo(() => {
        return getChartConfig({
            type: "line",
            metric: EngagementVerticals[selectedMetric],
            filter: isPercentage
                ? EngagementVerticals[selectedMetric].percentFilter
                : EngagementVerticals[selectedMetric].filter,
            data: transformedData,
            timeGranularity: selectedGranularity.value,
            durationObject,
            showChangeColumn: !(selectedMetric === EngagementVerticals.Visits.name && isPercentage),
            invertTooltipColor: EngagementVerticals[selectedMetric].invertChange,
        });
    }, [transformedData, selectedMetric, selectedGranularity, durationObject]);

    const toggleSelectedDisplay = (index: number) => {
        setSelectedDisplayTypeIndex(index);
        allTrackers.dispatchTrackEvent("Measure Button", "click", "Channel overview", {
            Value: index === EDisplayType.PERCENTAGE ? "percentage" : "number",
        });
    };

    const tabsContainerContent = useMemo(() => {
        return (
            <TabsContainer>
                <TabListStyled>
                    {Object.values(EngagementVerticals).map((metric, index) => {
                        const tabProps = getTabProps(index);
                        return (
                            <StyledTab
                                key={`tab-${index}`}
                                {...tabProps}
                            >
                                <StyledTabIcon
                                    size={"xs"}
                                    iconName={metric.icon}
                                />
                                <span>{i18nFilter()(metric.title)}</span>
                            </StyledTab>
                        );
                    })}
                </TabListStyled>
                {getUtilityButtons()}
            </TabsContainer>
        );
    }, []);

    const mmxGroupChartContainerContent = useMemo(() => {
        return (
            <MMXGroupChartContainer>
                {isLoading ? (
                    <PixelPlaceholderLoader
                        width="100%"
                        height="100%"
                    />
                ) : (
                    <FlexRow>
                        <GroupGraphContainer className={"sharedTooltip"}>
                            {isEmpty(transformedData) ? (
                                <EmptyState title={i18nFilter()("global.nodata.notavilable")} />
                            ) : (
                                <Chart
                                    type={"line"}
                                    config={chartConfig}
                                    data={transformedData}
                                />
                            )}
                        </GroupGraphContainer>
                        <CompareGraphLegendContainer>
                            <DropdownFilterContainer>{getDropdown}</DropdownFilterContainer>
                            <LegendsTitle>
                                {i18nFilter()("segments.mmx.group.graph.legends.channels.title")}
                            </LegendsTitle>
                            <SegmentDomainLegends
                                {...{
                                    groups,
                                    selectedDisplayTypeIndex,
                                    data,
                                    selectedMetric,
                                    selectedRows,
                                    allSegments,
                                    currentChannel,
                                    hiddenLegends,
                                    setHiddenLegends,
                                    params,
                                }}
                            />
                        </CompareGraphLegendContainer>
                    </FlexRow>
                )}
            </MMXGroupChartContainer>
        );
    }, [
        isLoading,
        groups,
        selectedDisplayTypeIndex,
        data,
        selectedMetric,
        selectedRows,
        allSegments,
        currentChannel,
        hiddenLegends,
        setHiddenLegends,
        params,
        chartConfig,
        transformedData,
    ]);

    const tabPanels = useMemo(() => {
        return Object.keys(EngagementVerticals).map((tab, idx) => (
            <TabPanel key={`tab-panel-${idx}`}>
                {idx === selectedTab ? <SectionContainer>{mmxGroupChartContainerContent}</SectionContainer> : null}
            </TabPanel>
        ));
    }, [selectedTab, mmxGroupChartContainerContent]);

    const engagementTabsContent = (
        <CompareContentContainer>
            <Tabs
                onSelect={onTabSelect}
                selectedIndex={selectedTab}
                forceRenderTabPanel={true}
            >
                {tabPanels}
            </Tabs>
        </CompareContentContainer>
    );

    return (
        <MMXCompareBoxContainer ref={chartRef}>
            <StyledTitleContainer>
                <FlexColumn>
                    <StyledHeaderTitle>
                        <BoxTitle tooltip={i18nFilter()("segments.analysis.mmx.group.graph.title.tooltip")}>
                            {i18nFilter()("segments.analysis.mmx.group.graph.title")}
                        </BoxTitle>
                    </StyledHeaderTitle>
                    <StyledBoxSubtitle>
                        <BoxSubtitle filters={subtitleFilters} />
                    </StyledBoxSubtitle>
                </FlexColumn>
                <HeaderControls>
                    <HeaderSwitchersGroup>
                        {selectedMetric === EngagementVerticals.Visits.name ? (
                            <SwitcherContainer>
                                <Switcher
                                    customClass="CircleSwitcher"
                                    className="rightSwitcher"
                                    selectedIndex={selectedDisplayTypeIndex}
                                    onItemClick={toggleSelectedDisplay}
                                >
                                    {displayTypeItems.map((displayTypeItem) => (
                                        <CircleSwitcherItem key={displayTypeItem.name}>
                                            {displayTypeItem.display}
                                        </CircleSwitcherItem>
                                    ))}
                                </Switcher>
                            </SwitcherContainer>
                        ) : null}
                        <SwitcherGranularityContainer
                            itemList={availableGranularities}
                            selectedIndex={selectedGranularity.index}
                            customClass={"CircleSwitcher"}
                            className={"gran-switch"}
                            onItemClick={toggleGranularity}
                        />
                    </HeaderSwitchersGroup>
                    {webSource === devicesTypes.TOTAL ? getUtilityButtons() : null}
                </HeaderControls>
            </StyledTitleContainer>
            {webSource !== devicesTypes.TOTAL ? (
                <>
                    {tabsContainerContent}
                    {engagementTabsContent}
                </>
            ) : (
                <TotalChartContainer>{mmxGroupChartContainerContent}</TotalChartContainer>
            )}
        </MMXCompareBoxContainer>
    );
};

const transformData = (data: Record<SegmentId, Record<string, IDataItem>>, isPercentage?: boolean) => {
    return Object.keys(data).map((chartKey: string, index) => {
        const chartData = data[chartKey];
        const { domain, color, rowIndex, seriesSubtitle }: any = Object.values(chartData)[0];
        const baseSeriesConfig: any = {
            name: domain,
            seriesSubtitle: seriesSubtitle,
            color,
            tooltipIndex: index,
            rowIndex,
        };
        return combineConfigs(
            {
                ...baseSeriesConfig,
                isDataSingleSeries: true,
                data: Object.entries(chartData).map((dataPoint: any) => {
                    const key = dataPoint?.[0];
                    dataPoint = dataPoint?.[1];
                    if (!dataPoint) {
                        return;
                    }
                    const confidenceLevel = dataPoint?.Value?.Confidence;
                    let value = isPercentage ? dataPoint.Value?.Percentage : dataPoint.Value?.Value;
                    if (isNaN(parseFloat(value))) {
                        value = null;
                    }

                    return [
                        dateToUTC(key),
                        value,
                        {
                            partial: confidenceLevel >= 0.3 && confidenceLevel < 1,
                            hallowMarker: confidenceLevel >= 0.3 && confidenceLevel < 1,
                            confidenceLevel,
                        },
                    ];
                }),
            },
            [baseSeriesConfig, markerWithDashedConfig],
        );
    });
};
